/* Browser-only helper for public tools. File bytes never leave the page. */
type Dimension = { label: string; width: number; height: number };
type Result = { file: File; width: number; height: number; matches: Dimension[] };

const root = document.querySelector<HTMLElement>('[data-size-checker]');
if (root) {
  const input = root.querySelector<HTMLInputElement>('#size-checker-files');
  const drop = root.querySelector<HTMLElement>('[data-checker-drop]');
  if (drop) drop.hidden = false;
  const results = root.querySelector<HTMLElement>('[data-checker-results]');
  const status = root.querySelector<HTMLElement>('[data-checker-status]');
  const clear = root.querySelector<HTMLButtonElement>('[data-checker-clear]');
  const dimensions = JSON.parse(root.dataset.dimensions ?? '[]') as Dimension[];
  const checked = new Map<string, Result>();
  let generation = 0, pending = 0;
  const read = (file: File): Promise<Result> => new Promise((resolve, reject) => {
    if (!['image/png', 'image/jpeg'].includes(file.type)) return reject(new Error(`${file.name}: choose a PNG or JPEG.`));
    if (file.size > 20 * 1024 * 1024) return reject(new Error(`${file.name}: maximum file size is 20 MB.`));
    const url = URL.createObjectURL(file), image = new Image();
    const cleanup = () => { URL.revokeObjectURL(url); image.onload = null; image.onerror = null; window.clearTimeout(timer); };
    const timer = window.setTimeout(() => { cleanup(); image.src = ''; reject(new Error(`${file.name}: image took too long to read.`)); }, 15000);
    image.onload = () => { cleanup(); resolve({ file, width: image.naturalWidth, height: image.naturalHeight, matches: dimensions.filter(size => size.width === image.naturalWidth && size.height === image.naturalHeight) }); };
    image.onerror = () => { cleanup(); reject(new Error(`${file.name}: image could not be read.`)); };
    image.src = url;
  });
  const paint = (message = '') => {
    results?.replaceChildren(...[...checked.values()].map(result => {
      const item = document.createElement('li'); item.className = 'checker-result';
      item.innerHTML = '<strong></strong><span></span><em></em>';
      item.querySelector('strong')!.textContent = result.file.name;
      item.querySelector('span')!.textContent = `${result.width} × ${result.height}px`;
      item.querySelector('em')!.textContent = result.matches.length ? result.matches.map(size => size.label).join(', ') : 'No exact AppScreenDesign preset';
      return item;
    }));
    if (clear) clear.hidden = checked.size === 0 && pending === 0;
    if (status) status.textContent = [checked.size ? `${checked.size} image${checked.size === 1 ? '' : 's'} checked locally.` : '', pending ? 'Checking images…' : '', message].filter(Boolean).join(' ');
  };
  // Serialize decoding; cap total work, including overlapping file-picker/drop events.
  let queue = Promise.resolve();
  const add = (files: FileList | File[]) => {
    const batch = [...files], current = generation, errors: string[] = [];
    const room = Math.max(0, 30 - checked.size - pending), selected = batch.slice(0, room);
    if (selected.length < batch.length) errors.push('Check up to 30 images at once. Clear results to start again.');
    pending += selected.length; paint(errors.join(' '));
    queue = queue.then(async () => {
      for (const file of selected) {
        if (current !== generation) return;
        try {
          const result = await read(file);
          if (current !== generation) return;
          checked.set(`${file.name}:${file.size}:${file.lastModified}`, result);
        } catch (error) { if (current !== generation) return; errors.push(error instanceof Error ? error.message : 'Image could not be checked.'); }
        pending--; paint(errors.join(' '));
      }
      if (current === generation) paint(errors.join(' '));
    });
  };
  root.querySelector('[data-choose-files]')?.addEventListener('click', () => input?.click());
  input?.addEventListener('change', () => { if (input.files) add(input.files); input.value = ''; });
  drop?.addEventListener('dragover', event => { event.preventDefault(); drop.classList.add('dragging'); });
  drop?.addEventListener('dragleave', () => drop.classList.remove('dragging'));
  drop?.addEventListener('drop', event => { event.preventDefault(); drop.classList.remove('dragging'); add(event.dataTransfer?.files ?? []); });
  clear?.addEventListener('click', () => { generation++; pending = 0; checked.clear(); paint('Results cleared.'); });
}

document.querySelectorAll<HTMLButtonElement>('[data-copy-endpoint]').forEach(button => button.addEventListener('click', async () => {
  const input = document.querySelector<HTMLInputElement>('#public-mcp-url'); const status = document.querySelector<HTMLElement>('[data-copy-feedback]');
  if (!input || !status) return;
  try { if (!navigator.clipboard) throw new Error('Clipboard unavailable'); await navigator.clipboard.writeText(input.value); status.textContent = 'MCP URL copied.'; }
  catch { input.focus(); input.select(); status.textContent = 'Select and copy the URL above.'; }
}));

const library = document.querySelector<HTMLElement>('.public-library');
if (library) {
  const controls = library.querySelector<HTMLElement>('form'); if (controls) controls.hidden = false;
  const search = library.querySelector<HTMLInputElement>('[data-template-search]');
  const count = library.querySelector<HTMLElement>('[data-template-count]');
  const empty = library.querySelector<HTMLElement>('[data-template-empty]');
  const cards = [...library.querySelectorAll<HTMLElement>('[data-template-card]')];
  let direction = 'all';
  const paint = () => {
    const query = search?.value.trim().toLowerCase() ?? '';
    let visible = 0;
    for (const card of cards) {
      const matches = (!query || (card.dataset.search ?? '').includes(query)) && (direction === 'all' || card.dataset.direction === direction);
      card.hidden = !matches; if (matches) visible++;
    }
    if (count) count.textContent = `${visible} editable composition${visible === 1 ? '' : 's'}`;
    if (empty) empty.hidden = visible !== 0;
  };
  library.querySelector('form')?.addEventListener('submit', event => event.preventDefault());
  search?.addEventListener('input', paint);
  library.querySelectorAll<HTMLButtonElement>('[data-template-filter]').forEach(button => button.addEventListener('click', () => {
    direction = button.dataset.templateFilter ?? 'all';
    library.querySelectorAll('[data-template-filter]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    paint();
  }));
  paint();
}
